import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  TextField,
  Autocomplete,
  Box,
  Typography,
  Chip,
  CircularProgress,
  InputAdornment,
  Avatar,
  Popper,
  styled,
  alpha
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import DirectionsCarRoundedIcon from '@mui/icons-material/DirectionsCarRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded';
import SearchOffRoundedIcon from '@mui/icons-material/SearchOffRounded';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { api } from 'common';
import { useTranslation } from 'react-i18next';
import {
  getUserDisplayName,
  getUserTypeLabel,
  getUserNavigationUrl,
  filterUsersByPermission
} from '../utils/searchUtils';
import { tokens as c, toneChipSx } from '../theme';
import { formatPhoneDisplay } from './modern/tableUtils';

// Suggestion list cap — keep it small/standard so the dropdown stays scannable.
const QUICK_SEARCH_LIMIT = 8;

// Who may open the add-customer form (the /users/edituser route's own list).
const ADD_CUSTOMER_ROLES = ['admin', 'fleetadmin', 'callcentersupervisor', 'callcenteragent'];

// The two rows that are not people: "Add … as a new customer" and "No match".
const ADD_ROW = '__add_customer';
const NONE_ROW = '__no_match';

// An Ethiopian mobile typed any usual way (0911…, 911…, +251 911…) as its 9
// plain digits ("911234567"), or '' when the text is not a whole mobile.
const mobileDigits = (query) => {
  const q = String(query || '').trim();
  if (!/^[0-9+\s().-]+$/.test(q)) return '';
  let d = q.replace(/\D/g, '');
  if (d.indexOf('251') === 0) d = d.slice(3);
  if (d.indexOf('0') === 0) d = d.slice(1);
  return /^[79]\d{8}$/.test(d) ? d : '';
};

// The people already loaded that match the text: name, email, or (3 digits
// or more) the phone number typed any usual way — 0923…, 923…, +251 923….
// searchUtils.userMatchesQuery is not used here: for a text without digits
// its phone test compares against '' and matches everyone, which MUI's own
// label filter used to hide (and with it every phone and email match).
const localDigits = (digits) => {
  let d = digits;
  if (d.indexOf('251') === 0) d = d.slice(3);
  if (d.indexOf('0') === 0) d = d.slice(1);
  return d;
};
const matchesText = (user, query) => {
  const text = String(query || '').trim().toLowerCase().replace(/\s+/g, ' ');
  if (!user || !text) return false;
  const name = `${user.firstName || ''} ${user.lastName || ''}`.trim().toLowerCase().replace(/\s+/g, ' ');
  if (name && name.includes(text)) return true;
  if (user.email && String(user.email).toLowerCase().includes(text)) return true;
  if (/^[0-9+\s().-]+$/.test(text)) {
    const want = localDigits(text.replace(/\D/g, ''));
    const have = String(user.mobile || '').replace(/\D/g, '');
    if (want.length >= 3 && have.includes(want)) return true;
  }
  return false;
};
const findLoaded = (users, query, role, cap) => {
  const q = String(query || '').trim().toLowerCase();
  const found = filterUsersByPermission(users, role).filter((u) => matchesText(u, query));
  const rank = (u) => {
    const name = getUserDisplayName(u).toLowerCase();
    if (name === q) return 0;
    return name.startsWith(q) ? 1 : 2;
  };
  found.sort((a, b) => rank(a) - rank(b) || getUserDisplayName(a).localeCompare(getUserDisplayName(b)));
  return found.slice(0, cap);
};

// No spaces, shown as stored (owner, 29 Oct: "+251912345678", never the
// spaced "+251 91 234 5678").
const plainMobile = (digits) => `+251${digits}`;
const formatPhone = (mobile) => formatPhoneDisplay(mobile);

// variant="bar" (default): the white search pill on the orange bar, as
// W2_Portal_Shell draws it; it fills the slot the shell gives it.
// variant="translucent": the older see-through field (white text on a
// coloured bar), 300 px wide from md up.
const FIELD_SX = {
  bar: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: c.WHITE,
      height: 40,
      borderRadius: '10px',
      boxShadow: 'none',
      '@media (pointer: coarse)': { height: 44 },
      '&:hover': { backgroundColor: c.WHITE },
      '&.Mui-focused': { boxShadow: '0 0 0 3px rgba(255,255,255,0.45)' },
      '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    },
    '& .MuiInputBase-input': {
      color: c.INK,
      fontSize: 14,
      padding: '8px 12px',
      '@media (pointer: coarse)': { fontSize: 16 },
      '&::placeholder': { color: c.TEXT_3, opacity: 1 },
    },
    '& .MuiInputAdornment-root': { marginRight: 0 },
    '& .MuiInputAdornment-root .MuiSvgIcon-root': { color: c.INK_2 },
    '& .MuiAutocomplete-endAdornment .MuiSvgIcon-root': { color: c.INK_2 },
    '& .MuiCircularProgress-root': { color: c.ACCENT },
  },
  translucent: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: alpha(c.WHITE, 0.15),
      borderRadius: '20px',
      height: 36,
      '&:hover': { backgroundColor: alpha(c.WHITE, 0.25) },
      '&.Mui-focused': {
        backgroundColor: c.WHITE,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      },
      '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    },
    '& .MuiInputBase-input': {
      color: c.WHITE,
      padding: '8px 12px',
      '&::placeholder': { color: alpha(c.WHITE, 0.85), opacity: 1 },
    },
    '& .Mui-focused .MuiInputBase-input': { color: c.INK },
    '& .MuiInputAdornment-root': { marginRight: 0 },
    '& .MuiInputAdornment-root .MuiSvgIcon-root': { color: c.WHITE },
    '& .Mui-focused .MuiInputAdornment-root .MuiSvgIcon-root': { color: c.INK_2 },
  },
};

const ROOT_SX = {
  bar: { position: 'relative', width: '100%' },
  translucent: { position: 'relative', width: { xs: '100%', md: '300px' }, mx: { xs: 2, md: 0 } },
};

const StyledPopper = styled(Popper)(({ theme }) => ({
  '& .MuiPaper-root': {
    marginTop: theme.spacing(1),
    boxShadow: '0 6px 20px rgba(15,23,42,0.16)',
    borderRadius: 12,
    maxHeight: '400px',
    overflowY: 'auto',
  },
}));

// Icon on a tinted circle per kind of person (never colour alone: the chip
// beside the name says the kind in words).
const kindOf = (usertype) => {
  if (usertype === 'driver') return 'driver';
  if (usertype === 'customer' || usertype === 'rider') return 'customer';
  return 'other';
};
const KIND_STYLE = {
  driver: { fg: c.ACCENT_TEXT, bg: c.ACCENT_TINT, tone: 'accent' },
  customer: { fg: c.INFO_TEXT, bg: c.INFO_TINT, tone: 'info' },
  other: { fg: c.INK_2, bg: c.SURFACE_ALT, tone: 'neutral' },
};

const circleSx = (fg, bg) => ({ width: 36, height: 36, bgcolor: bg, color: fg, '& .MuiSvgIcon-root': { fontSize: 20 } });

const QuickSearch = ({ variant = 'bar', autoFocus = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedValue, setSelectedValue] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const searchTimeout = useRef(null);
  const dispatch = useDispatch();
  const look = FIELD_SX[variant] ? variant : 'bar';

  // Redux state
  const auth = useSelector(state => state.auth);
  const usersData = useSelector(state => state.usersdata);
  const role = auth?.profile?.usertype;
  // When on, customers aren't pre-loaded (call-center) — resolve them via an
  // indexed server query instead of the in-memory array.
  const useOnDemand = useSelector(state => state.settingsdata?.settings?.useCustomerOnDemand === true);
  const canAddCustomer = ADD_CUSTOMER_ROLES.includes(role);

  useEffect(() => () => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
  }, []);

  // Debounced search: drivers/staff are matched in-memory; customers are
  // resolved on-demand from /customer_index (first / first+last / phone) when
  // they're no longer pre-loaded. Results merged, deduped, capped. Nothing
  // found -> one row that says so, or (a whole mobile number, for staff who
  // may add people) "Add … as a new customer" (Q19).
  const performSearch = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      setOpen(false);
      return;
    }
    setIsLoading(true);
    try {
      // Keep the legacy cap (15) for the full-load path so flag-OFF behavior is
      // unchanged; use the smaller suggestion cap only on the on-demand path.
      const cap = useOnDemand ? QUICK_SEARCH_LIMIT : 15;
      const local = (usersData?.users && Array.isArray(usersData.users))
        ? findLoaded(usersData.users, query, role, cap)
        : [];
      let customers = [];
      if (useOnDemand) {
        const isPhone = /^[0-9+\s-]+$/.test(query.trim());
        if (isPhone) {
          const found = await dispatch(api.lookupCustomerByPhone(query));
          if (found) customers = [found];
        } else {
          customers = (await dispatch(api.lookupCustomersByName(query, 5))) || [];
        }
      }
      const byId = {};
      [].concat(local, customers).forEach(u => { if (u && u.id && !byId[u.id]) byId[u.id] = u; });
      const merged = filterUsersByPermission(Object.keys(byId).map(k => byId[k]), role).slice(0, cap);
      if (merged.length) {
        setSearchResults(merged);
      } else {
        const digits = mobileDigits(query);
        setSearchResults([
          digits && canAddCustomer
            ? { id: ADD_ROW, mobile: digits }
            : { id: NONE_ROW, query: query.trim() },
        ]);
      }
      setOpen(true);
    } catch (_) {
      setSearchResults([]);
      setOpen(false);
    } finally {
      setIsLoading(false);
    }
  }, [usersData?.users, role, useOnDemand, dispatch, canAddCustomer]);

  // Handle search input change with debouncing
  const handleSearchChange = (event, value, reason) => {
    // A chosen option writes its label into the box ('reset'): keep what was
    // typed instead, the list is about to close anyway.
    if (reason === 'reset') return;
    // Clear search when user clears the input or presses escape
    if (reason === 'clear' || reason === 'escape') {
      setSearchQuery('');
      setSelectedValue(null);
      setSearchResults([]);
      setOpen(false);
      return;
    }

    setSearchQuery(value || '');

    // If value is empty, clear everything
    if (!value || value.trim() === '') {
      setSelectedValue(null);
      setSearchResults([]);
      setOpen(false);
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
      return;
    }

    // Clear previous timeout
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    // Set new timeout for debounced search
    searchTimeout.current = setTimeout(() => {
      performSearch(value);
    }, 300);
  };

  const resetSearch = () => {
    setSearchQuery('');
    setSelectedValue(null);
    setSearchResults([]);
    setOpen(false);
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
  };

  // Handle user selection
  const handleUserSelect = (event, user) => {
    if (user && typeof user === 'object' && user.id === ADD_ROW) {
      // The add-customer form; the number goes along as plain digits.
      resetSearch();
      navigate('/users/edituser/customer', { state: { mobile: user.mobile } });
    } else if (user && typeof user === 'object' && user.id && user.id !== NONE_ROW) {
      const url = getUserNavigationUrl(user);
      // Clear all search state
      resetSearch();
      // Navigate to user details
      navigate(url);
    } else if (!user) {
      // User cleared the selection
      setSelectedValue(null);
    }
  };

  const typeLabel = (usertype) => {
    const kind = kindOf(usertype);
    if (kind === 'driver') return t('driver', { defaultValue: 'Driver' });
    if (kind === 'customer') return t('customer', { defaultValue: 'Customer' });
    return t(usertype, { defaultValue: getUserTypeLabel(usertype) });
  };

  const peopleFound = searchResults.length > 0 && searchResults[0].id !== ADD_ROW && searchResults[0].id !== NONE_ROW;

  // Don't render for roles that can't search
  if (!role || ['driver', 'customer', 'rider'].includes(role)) {
    return null;
  }

  const placeholder = t('quick_search_placeholder');

  // MUI hands each row its props with a key inside; React wants the key
  // passed on its own, not spread.
  const rowProps = (props) => {
    const { key, ...rest } = props || {};
    return rest;
  };

  const renderPerson = (props, option) => {
    const kind = KIND_STYLE[kindOf(option.usertype)];
    const name = getUserDisplayName(option);
    return (
      <Box component="li" key={option.id} {...rowProps(props)} sx={{ gap: 1.5, py: '8px !important', alignItems: 'center' }}>
        <Avatar aria-hidden="true" sx={circleSx(kind.fg, kind.bg)}>
          {option.usertype === 'driver' ? <DirectionsCarRoundedIcon /> : <PersonOutlineRoundedIcon />}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1, rowGap: 0.25 }}>
            <Typography component="span" sx={{ fontSize: 15, fontWeight: 600, lineHeight: 1.4, color: c.INK, overflowWrap: 'anywhere' }}>
              {name === 'Unknown User' ? t('no_name', { defaultValue: 'No name' }) : name}
            </Typography>
            <Chip
              label={typeLabel(option.usertype)}
              size="small"
              sx={{ ...toneChipSx(kind.tone), height: 22, fontSize: 12 }}
            />
          </Box>
          {option.mobile ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
              <PhoneRoundedIcon aria-hidden="true" sx={{ fontSize: 14, color: c.INK_2 }} />
              <Typography component="span" sx={{ fontSize: 13, lineHeight: 1.45, color: c.INK_2, fontVariantNumeric: 'tabular-nums' }}>
                {formatPhone(option.mobile)}
              </Typography>
            </Box>
          ) : null}
        </Box>
      </Box>
    );
  };

  const renderAddRow = (props, option) => (
    <Box component="li" key={option.id} {...rowProps(props)} sx={{ gap: 1.5, py: '10px !important', alignItems: 'center' }}>
      <Avatar aria-hidden="true" sx={circleSx(c.SUCCESS_TEXT, c.SUCCESS_TINT)}>
        <PersonAddAltRoundedIcon />
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography component="span" sx={{ display: 'block', fontSize: 15, fontWeight: 600, lineHeight: 1.4, color: c.INK }}>
          {t('quick_search_add_customer', { phone: plainMobile(option.mobile), defaultValue: 'Add {{phone}} as a new customer' })}
        </Typography>
        <Typography component="span" sx={{ display: 'block', fontSize: 13, lineHeight: 1.45, color: c.INK_2 }}>
          {t('quick_search_no_one_has_number', { defaultValue: 'No driver or customer has this number.' })}
        </Typography>
      </Box>
    </Box>
  );

  const renderNoMatch = (props, option) => (
    <Box component="li" key={option.id} {...rowProps(props)} sx={{ gap: 1.5, py: '10px !important', alignItems: 'center', opacity: '1 !important' }}>
      <Avatar aria-hidden="true" sx={circleSx(c.INK_2, c.SURFACE_ALT)}>
        <SearchOffRoundedIcon />
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography component="span" sx={{ display: 'block', fontSize: 15, fontWeight: 600, lineHeight: 1.4, color: c.INK, overflowWrap: 'anywhere' }}>
          {t('quick_search_no_match', { query: option.query, defaultValue: 'No drivers or customers match “{{query}}”' })}
        </Typography>
        <Typography component="span" sx={{ display: 'block', fontSize: 13, lineHeight: 1.45, color: c.INK_2 }}>
          {t('quick_search_no_match_hint', { defaultValue: 'Try part of the name or the whole phone number.' })}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={ROOT_SX[look]}>
      <Autocomplete
        value={selectedValue}
        open={open}
        onOpen={() => {
          if (searchResults.length > 0) {
            setOpen(true);
          }
        }}
        onClose={() => {
          setOpen(false);
          // Clear results when closing the dropdown
          setSearchResults([]);
        }}
        inputValue={searchQuery}
        onInputChange={handleSearchChange}
        onChange={handleUserSelect}
        options={searchResults}
        // The search above already matched name, phone and email; MUI's own
        // filter (label contains the text) hid every phone and email match.
        filterOptions={(options) => options}
        getOptionDisabled={(option) => option.id === NONE_ROW}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        loading={isLoading}
        getOptionLabel={(option) => {
          if (!option) return '';
          if (typeof option === 'string') return option;
          if (option.id === ADD_ROW || option.id === NONE_ROW) return searchQuery;
          return getUserDisplayName(option);
        }}
        PopperComponent={StyledPopper}
        clearOnEscape
        clearOnBlur={false}
        blurOnSelect
        disableClearable={false}
        // The X shows whenever something is typed, not only on hover or
        // focus (call centre, 29 Oct: an X on every search box).
        clearText={t('clear_search', { defaultValue: 'Clear search' })}
        sx={{ '& .MuiAutocomplete-clearIndicator': { visibility: 'visible' } }}
        autoComplete
        autoHighlight
        freeSolo
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            size="small"
            autoFocus={autoFocus}
            sx={FIELD_SX[look]}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                resetSearch();
                e.target.blur();
              }
            }}
            inputProps={{
              ...params.inputProps,
              'aria-label': placeholder,
            }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {isLoading && <CircularProgress color="inherit" size={20} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => {
          if (option.id === ADD_ROW) return renderAddRow(props, option);
          if (option.id === NONE_ROW) return renderNoMatch(props, option);
          return renderPerson(props, option);
        }}
        groupBy={peopleFound ? (option) => {
          if (option.usertype === 'driver') return t('drivers');
          if (option.usertype === 'customer' || option.usertype === 'rider') return t('customers');
          return t('others');
        } : undefined}
        // The list lives in a popper outside this box, so its group labels
        // are styled through the paper (dark orange on the orange tint).
        componentsProps={{
          paper: {
            sx: {
              '& .MuiAutocomplete-groupLabel': {
                backgroundColor: c.ACCENT_TINT,
                color: c.ACCENT_TEXT,
                fontWeight: 700,
                fontSize: 13,
                lineHeight: 1.45,
                padding: '6px 16px',
                position: 'sticky',
                top: 0,
                zIndex: 1,
              },
            },
          },
        }}
      />
    </Box>
  );
};

export default QuickSearch;
